import React, { useEffect, useRef, useState } from "react";
import { TextField, Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Avatar from "@mui/material/Avatar";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { ErrorMessage } from "formik";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SelectInputComponentProps } from "./select-input-component.props";
import "./select-input-component.scss";
import "../../../assets/styles/variables.scss";
import { makeStyles } from "@mui/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "../../../assets/icons/closeIcon.svg";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import CancelIcon from "@mui/icons-material/Cancel"; // close icon style

export interface OwnerRoleOption {
  role: string;
}
/**
 * SelectInputComponent wraps MUI Select with optional multi-select, avatar/image labels,
 * built-in search, and custom renderValue logic. Controlled via value/onChange.
 */
const SelectInputDropDownComponent: React.FC<SelectInputComponentProps> = ({
  fieldName,
  label,
  multiple = false,
  disabled,
  value,
  onChange,
  options = [],
  isShowError = false,
  showLabelInSelect = false,
  placeholder,
  handleBlur,
  notEditable,
  icon,
  closeIcon,
  onClear,
  tabs = false,
  isRequired = false,
  isOwnerDropdown = false,
  onRoleChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    keepMounted: true,
    slotProps: {
      paper: {
        sx: {
          paddingTop: multiple ? "0px !important" : "4px !important",
        },
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        },
      },
    },
  };

  /** ✅ Simplified handleChange — works with multiple/single select */
  const handleChange = (event: any) => {
    const { value: selectedValue } = event.target;

    if (multiple) {
      const normalizedValue = Array.isArray(selectedValue)
        ? selectedValue
        : [selectedValue];
      onChange(normalizedValue);
      return;
    }

    onChange(selectedValue);
  };

  /** ✅ Avatar initials generator */
  const stringAvatar = (name: string) => {
    const parts = name?.split(" ");
    const initials = (parts?.[0]?.[0] || "") + (parts?.[1]?.[0] || "");
    return {
      sx: {
        bgcolor: "#403c6f",
        width: 40,
        height: 40,
      },
      children: initials,
    };
  };

  const mainOptions= [...options?.filter((option: any) => value?.length ? value?.includes(option?.value) : false) || [], ...options?.filter((option: any) => value?.length ? !value?.includes(option?.value) : true) || []]
  const filteredOptions = mainOptions.filter((option: any) =>{    
        return  (option.label?.toLowerCase()
      .includes(searchTerm.toLowerCase()) ?? option.value?.toLowerCase()
      .includes(searchTerm.toLowerCase()))
  }) || [];

  //To remove  default background color for menuItem
  const useStyles = makeStyles(() => ({
    selected: {},
    rootMenuItem: {
      "&$selected": {
        backgroundColor: "transparent !important",
        "&:hover": {
          backgroundColor: "#8879501A !important",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
      "&.Mui-focusVisible": {
        backgroundColor: "transparent !important",
      },
      "&.Mui-selected": {
        backgroundColor: "transparent !important",
      },
      "&.Mui-selected:focus": {
        backgroundColor: "transparent !important",
      },
    },
  }));
  const classes = useStyles();

  /** ✅ Fix 1: Control open state correctly */
  const [open, setOpen] = useState(false);

  /** ✅ Clear search term when dropdown closes */
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    }
  }, [open]);

  /** ✅ Fix 2: Allow checkbox clicks to select values */
  const handleItemClick = (event: React.MouseEvent, optionValue: any) => {
    event.stopPropagation(); // Prevent MUI from closing immediately
    if (multiple) {
      let newValue = Array.isArray(value) ? [...value] : [];
      if (newValue.includes(optionValue)) {
        newValue = newValue.filter((v) => v !== optionValue);
      } else {
        newValue.push(optionValue);
      }
      onChange(newValue);
      // Refocus search input after selection in multiple mode
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      onChange(optionValue);
      setOpen(false); // close dropdown for single select
    }
  };

  /** ✅ Fix 3: Close icon inside dropdown header */
  const handleCloseDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    if (handleBlur) handleBlur();
  };

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open, value, searchTerm]);

  const slotsPaperStatus = (props: any) => {
    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {props.children}
      </div>
    );
  };

  const handleDeleteItemClick = (event: React.MouseEvent, optionValue: any) => {
    event.stopPropagation(); // Prevent MUI from closing immediately
    
    if (multiple) {
      let newValue = Array.isArray(value) ? [...value] : [];
      if (newValue.includes(optionValue)) {
        newValue = newValue.filter((v) => v !== optionValue);
      }
      onChange(newValue);
      // Refocus search input after selection in multiple mode
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    } else {
      onChange(optionValue);
      setOpen(false); // close dropdown for single select
    }
  };
  
  return (
    <Box>
      <div
        className={`form-select-input-component form-select-input-dropdown-component ${isRequired ? "required" : ""}`}
      >
        {label && !showLabelInSelect && (
          <InputLabel htmlFor={fieldName}>{label}</InputLabel>
        )}

        <FormControl fullWidth>
          {showLabelInSelect && (
            <InputLabel htmlFor={fieldName}>{label}</InputLabel>
          )}

          <Select
            labelId={`${fieldName}-label`}
            id={fieldName}
            multiple={multiple}
            displayEmpty
            value={value || (multiple ? [] : "")}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={handleChange}
            input={<OutlinedInput label="Tag" />}
            IconComponent={icon ? () => null : ExpandMoreIcon}
            MenuProps={MenuProps}
            disabled={notEditable}
            renderValue={(selected) => {
              if (
                !selected ||
                (Array.isArray(selected) && selected.length === 0)
              ) {
                return <span className="placeholder">{placeholder}</span>;
              }

              if (isOwnerDropdown && Array.isArray(selected)) {
                const profileImages = selected
                  .map((item: any) =>
                    options.find((opt: any) => opt.value === item),
                  )
                  .filter(Boolean);
                return (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      overflowY: "hidden",
                      whiteSpace: "nowrap",
                      width: "100%",
                      maxWidth: "100%",
                      scrollbarWidth: "none",
                      scrollbarColor: "#ccc transparent",
                      "&::-webkit-scrollbar": {
                        height: "6px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "transparent",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#aaa",
                      },
                    }}
                  >
                    {profileImages.map((image: any) => (
                      <Chip
                        key={image.value}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        avatar={
                          image.image ? (
                            <img
                              src={image.image}
                              alt={image.label}
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Avatar {...stringAvatar(image.label)} />
                          )
                        }
                        label={image.label}
                        variant="outlined"
                        deleteIcon={<CancelIcon />}
                        onDelete={(e) => {
                          handleDeleteItemClick(e, image.value);
                        }}
                      />
                    ))}
                  </Stack>
                );
              }

              if (tabs && Array.isArray(selected)) {
                return (
                  <div className="selected-tabs-wrapper">
                    {selected.map((item) => (
                      <div key={item} className="selected-tab">
                        <span className="tab-label">{item}</span>
                        <button
                          className="remove-tab"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(selected.filter((i) =>//  NOSONAR
                                 i !== item));
                          }}
                        >
                          <div className="image-wrapper">
                            <img
                              alt="Close"
                              src={CloseIcon}
                              className="close-icon"
                              style={{
                                width: "12px",
                                height: "12px",
                                cursor: "pointer",
                              }}
                            />
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                );
              }

              return Array.isArray(selected) ? selected.join(", ") : selected;
            }}
          >
            {/* ✅ Search Header */}
            {multiple ? (
              <div
                key="search-bar"
                style={{
                  position: "sticky",
                  top: "0px",
                  width: "calc(100% - 8px)",
                  zIndex: 1,
                  background: "white",
                  padding: "20px 8px", // Reduced padding
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search..."
                  value={searchTerm}
                  inputRef={searchInputRef}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchTerm && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchTerm("");
                                // Refocus the input after clearing
                                setTimeout(() => {
                                  searchInputRef.current?.focus();
                                }, 0);
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                              }}
                            >
                              <img
                                src={CloseIcon}
                                alt="clear search"
                                style={{
                                  cursor: "pointer",
                                  marginRight: "8px",
                                  width: "16px",
                                  height: "16px",
                                }}
                              />
                            </button>
                          )}
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderRadius: "8px" },
                      "& input": { padding: "12px 10px" },
                    },
                  }}
                />
                <div className="image-wrapper">
                  <button
                    onClick={handleCloseDropdown}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                    }}
                  >
                    <img
                      alt="Close"
                      src={CloseIcon}
                      className="close-icon"
                      style={{ cursor: "pointer", paddingRight: "10px" }}
                    />
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  background: "white",
                  padding: "4px 8px", // Reduced padding
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end", // Align to right corner
                }}
              >
                <div className="image-wrapper">
                  <button
                    onClick={handleCloseDropdown}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <img
                      alt="Close"
                      src={CloseIcon}
                      className="close-icon"
                      style={{ cursor: "pointer" }}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* ✅ Options */}
            <OverlayScrollbarsComponent
              style={{ maxHeight: 300 }}
              options={{
                scrollbars: {
                  theme: "os-theme-dark",
                  autoHide: "scroll",
                  autoHideDelay: 400,
                },
              }}
              defer
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option: any) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={disabled || option?.disabled}
                    classes={{
                      selected: classes.selected,
                      root: classes.rootMenuItem,
                    }}
                    sx={{ bgcolor: Array.isArray(value) && value.includes(option.value) ? "#eae9fc" : "transparent" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                      }}
                    >
                      {multiple && (
                        <Checkbox
                          checked={
                            Array.isArray(value) && value.includes(option.value)
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            handleItemClick(
                              {
                                ...e,
                                stopPropagation: () => {},
                              } as unknown as React.MouseEvent,
                              option.value,
                            );
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {option.image && (
                        <img
                          src={option.image}
                          alt={option.label || option.value}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      {option.avatar && (
                        <Avatar {...stringAvatar(option.avatar)} />
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <ListItemText
                          primary={option.label ?? option.value}
                          slotProps={{
                            primary: isOwnerDropdown
                              ? {
                                  style: {
                                    fontWeight: "400 !important",
                                    fontSize: "14px",
                                    fontFamily: "Manrope, sans-serif",
                                  },
                                }
                              : {
                                  style: {
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    fontFamily: "Manrope, sans-serif",
                                  },
                                },
                          }}
                        />
                        {Array.isArray(value) &&
                        value.includes(option.value) ? (
                          <Autocomplete<OwnerRoleOption>
                            value={{ role: option.role }}
                            onChange={(event, newValue) => {
                              onRoleChange?.(event, newValue, option);
                            }}
                            options={[
                              {
                                role: "Owner",
                              },
                              {
                                role: "PM",
                              },
                            ]}
                            getOptionLabel={(options) => options.role}
                            isOptionEqualToValue={(options, value) =>
                              options.role === value.role
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label=""
                                placeholder="Select Status"
                              />
                            )}
                            sx={{
                              "& .MuiAutocomplete-option": {
                                fontSize: "14px",
                                fontFamily: "Manrope, sans-serif",
                              },
                            }}
                            slots={{
                              paper: (props) => slotsPaperStatus(props),
                            }}
                            slotProps={{
                              popper: {
                                sx: {
                                  "& .MuiAutocomplete-option": {
                                    fontSize: "14px",
                                    fontFamily: "Manrope, sans-serif",
                                  },
                                },
                              },
                            }}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No results found</MenuItem>
              )}
            </OverlayScrollbarsComponent>
          </Select>

          {/* ✅ External Clear Icon */}
          {!multiple && closeIcon && value && (
            <button
              className="select-clear-icon"
              onClick={() => {
                if (onChange) onChange("");
                if (onClear) onClear();
              }}
            >
              <div className="image-wrapper">
                <img
                  alt="Close"
                  src={CloseIcon}
                  className="close-icon"
                  style={{ cursor: "pointer" }}
                />
              </div>
            </button>
          )}
        </FormControl>

        {isShowError && (
          <ErrorMessage
            name={fieldName || ""}
            component="div"
            className="error-message"
          />
        )}
      </div>
    </Box>
  );
};

export default SelectInputDropDownComponent;
